import { AppFunctionComponent } from "../../types"
import React, { useContext } from "react"
import styled, { css } from "styled-components"
import { PrimaryHeading } from "../../components/common/heading.component"
import Section, {
  reduceEmptySpaces,
  SectionArticle,
  SectionHeading,
  SectionImage,
  SectionLayout,
} from "../../components/common/sections/section.component"
import SectionBackgroundWrapper from "../../helpers/sitebuilder-background-wrapper/section-background-wrapper"
import {
  EmbedType,
  TextWithEmbedBuilder,
  TextWithEmbedLayout,
} from "../../types"
import EmbedResolver from "../embed.resolver"
import RichText from "../rich-text/rich-text.resolver"
import { FootnoteContext } from "../../templates/page.template"
import { ScrollAnchor } from "../../components/common/sections/custom/innovation-lab-form/innovation-lab.component"
import { Link } from "gatsby"
import ShowOnlyOn from "../../components/common/show-only-on.component"

const SectionContainer = styled(Section)<{
  customStyle?: TextWithEmbedBuilder["style"]
  fullWidth?: boolean
}>`
  ${SectionArticle} {
    ${reduceEmptySpaces};
  }

  ${({ customStyle }) =>
    customStyle?.reduceMobileEmptyRows &&
    css`
      @media (max-width: ${({ theme }) =>
          theme.breakpoint.mobileHorizontal}px) {
        grid-template-rows: unset !important;
        ${SectionImage} {
          img {
            object-fit: contain !important;
          }
        }
      }
    `}

  ${({ customStyle }) =>
    customStyle?.desktopImageHeight &&
    css`
      @media (min-width: ${({ theme }) =>
          theme.breakpoint.mobileHorizontal}px) {
        ${SectionImage} {
          height: ${customStyle.desktopImageHeight};
        }
      }
    `}

  ${({ customStyle, theme }) =>
    customStyle?.mobileReversed &&
    css`
      @media (max-width: ${theme.breakpoint.mobileHorizontal}px) {
        grid-template-areas: "Image" "ImageDescription" "Heading" "Article";
        ${SectionImage} {
          margin-top: 0;
          margin-bottom: 48px;
        }
      }
    `}

    ${({ customStyle, theme }) =>
    customStyle?.mobileReversedTitleFirst &&
    css`
      @media (max-width: ${theme.breakpoint.mobileHorizontal}px) {
        grid-template-areas: "Heading" "Image" "ImageDescription" "Article";
        ${SectionHeading} {
          max-width: 300px;
        }
        ${SectionImage} {
          margin-top: 0px;
          margin-bottom: 10px;
        }
      }
    `}

  ${({ customStyle }) =>
    customStyle?.textMobileAlignCenter &&
    css`
      @media (max-width: ${({ theme }) =>
          theme.breakpoint.tabletHorizontal}px) {
        ${SectionArticle} {
          text-align: center;
        }
      }
    `}

    ${({ customStyle }) =>
    customStyle?.textDesktopAlignCenter &&
    css`
      @media (min-width: ${({ theme }) =>
          theme.breakpoint.tabletHorizontal}px) {
        ${SectionArticle} {
          text-align: center;
        }
      }
    `}

  ${({ customStyle }) =>
    customStyle?.responsive_image_max_width &&
    css`
      ${SectionImage} {
        max-width: ${customStyle.responsive_image_max_width}px;
        margin: auto;
      }
    `}

  ${({ customStyle }) => {
    return (
      customStyle?.text_margin_bottom !== undefined &&
      customStyle?.text_margin_bottom !== null &&
      css`
        ${SectionArticle} {
          margin-bottom: ${customStyle.text_margin_bottom}px !important;
        }
      `
    )
  }}

  ${({ customStyle }) =>
    customStyle?.font &&
    css`
      ${SectionArticle} {
        * {
          font-size: ${customStyle.font.fontSize ?? "inherit"};
          line-height: ${customStyle.font.lineHeight ?? "inherit"};
          font-weight: ${customStyle.font.fontWeight ?? "inherit"};
        }
      }
    `}

  ${({ customStyle }) =>
    customStyle?.mobileFont &&
    css`
      @media (max-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal}px) {
        ${SectionArticle} {
          * {
            font-size: ${customStyle.mobileFont.fontSize ?? "inherit"};
            line-height: ${customStyle.mobileFont.lineHeight ?? "inherit"};
            font-weight: ${customStyle.mobileFont.fontWeight ?? "inherit"};
          }
        }
      `}
  
      ${({ customStyle }) =>
    customStyle?.headingFont &&
    css`
      ${SectionArticle} {
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          font-size: ${customStyle.headingFont.fontSize ?? "inherit"};
          line-height: ${customStyle.headingFont.lineHeight ?? "inherit"};
          font-weight: ${customStyle.headingFont.fontWeight ?? "inherit"};
        }
      }
    `}

  ${({ customStyle }) =>
    customStyle?.mobileHeadingFont &&
    css`
      @media (max-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal}px) {
        ${SectionArticle} {
          h1,h2,h3,h4,h5,h6 {
            font-size: ${customStyle.mobileHeadingFont.fontSize ?? "inherit"};
            line-height: ${
              customStyle.mobileHeadingFont.lineHeight ?? "inherit"
            };
            font-weight: ${
              customStyle.mobileHeadingFont.fontWeight ?? "inherit"
            };
            margin-bottom: 30px;
          }
        }
      `}

  ${({ customStyle }) =>
    customStyle?.imageContain &&
    css`
      ${SectionImage} {
        img {
          height: unset !important;
          object-fit: contain;
        }
      }
    `}

  ${({ theme, customStyle }) =>
    customStyle &&
    css`
      ${customStyle.margin_top &&
      css`
        margin-top: ${customStyle.margin_top}px;
      `}
      background-color: ${customStyle.background};
      @media (min-width: ${theme.breakpoint.mobileHorizontal}px) {
        --text-max-width: ${customStyle.text_max_width}px;
      }

      ${SectionArticle} {
        img {
          max-width: ${customStyle.image_max_width}px;
          margin-top: ${50 / 16}em;
        }
      }

      ${customStyle.mobileImageHeight &&
      css`
        @media (max-width: ${({ theme }) =>
          theme.breakpoint.mobileHorizontal}px) {
          ${SectionImage} {
            height: ${customStyle.mobileImageHeight};
            min-height: 188px !important;
          img {
            object-fit: contain;
            height: unset !important;
        }
      `}

      ${customStyle.desktopImageCover &&
      css`
        @media (min-width: ${({ theme }) =>
            theme.breakpoint.mobileHorizontal}px) {
          img {
            object-fit: cover;
          }
        }
      `}
    `}

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      max-width: none;
    `};
`

const EmbedContainer = styled(EmbedResolver)`
  grid-area: Image;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    ${({ internal }) =>
      internal?.type === "ContentfulVideo" &&
      css`
        height: calc(100vw - 40px);
      `}

    img {
      height: calc(100vw - 40px);
    }
  }
`

const TextWithEmbedResolver: AppFunctionComponent<TextWithEmbedBuilder> = (
  section: TextWithEmbedBuilder
) => {
  const footnotesOptions = useContext(FootnoteContext)

  const resolveLayout = () => {
    switch (section.layout) {
      case TextWithEmbedLayout.RightText:
        return SectionLayout.ImageText
      case TextWithEmbedLayout.LeftText:
        return SectionLayout.TextImage
      case TextWithEmbedLayout.Wide:
        return SectionLayout.TextBigImage
      case TextWithEmbedLayout.WideReverse:
        return SectionLayout.TextBigImageReverse
      default:
        return SectionLayout.ImageText
    }
  }

  const isWideSlider = () => {
    return (
      section.layout === TextWithEmbedLayout.Wide &&
      section.embed?.internal?.type === EmbedType.Slider
    )
  }

  const renderContent = () => (
    <ShowOnlyOn showOnlyOn={section.showOnlyOn}>
      <SectionBackgroundWrapper customStyle={section.style}>
        <SectionContainer
          layout={resolveLayout()}
          customStyle={section.style}
          fullWidth={isWideSlider()}
          as="div"
        >
          <ScrollAnchor id={section.anchorId} />
          {section.title && (
            <SectionHeading>
              <PrimaryHeading>{section.title}</PrimaryHeading>
            </SectionHeading>
          )}
          {section.text && (
            <SectionArticle>
              <RichText content={section.text} options={footnotesOptions} />
            </SectionArticle>
          )}
          {section.embed && typeof section.embed === "object" && (
            <EmbedContainer {...section.embed} />
          )}
        </SectionContainer>
      </SectionBackgroundWrapper>
    </ShowOnlyOn>
  )

  if (section.externalLink) {
    return <Link to={section.externalLink}>{renderContent()}</Link>
  }

  return renderContent()
}

export default TextWithEmbedResolver
